import { useId, useState } from 'react';
import {
  alterarCampanha, alterarVertical, campanhaEditavel, codigoDaCampanha,
  type AlteracaoDaVertical, type CampanhaCompleta, type VerticalCompleta,
} from '../../api/cptu';
import { CONFORMIDADE, PREMISSAS_PADRAO, type Premissas } from '../../cptu/motor';
import {
  FORMULAS_DAS_PREMISSAS, colunasQueSaemDaHomologacao, type FormulaDaPremissa, type GrupoDePremissa,
} from '../../cptu/visual';
import { numero, paraCampo, paraNumero, plural } from '../../formato';
import { useSessao } from '../../sessao';
import { Area, Aviso, CampoNumerico, Dialogo, ErroDaApi } from '../../ui';
import { AcoesBloqueadas, motivoDoBloqueio } from './CampanhaMoldura';
import { FormularioDaJanela } from './FormularioDaJanela';
import {
  excecoesDaVertical, focarOPrimeiro, premissasParaACampanha, premissasParaAVertical, useFocoDeVolta, type Rascunho,
} from './interpretacao';

/**
 * «Simular» na tela da vertical — onda 313, Tarefa 313.4 (spec 07 §6.1, item 4; R7, R10, R14).
 *
 * As premissas que mudam o resultado, em rascunho: Nkt, NA, kOCR, φ'cv, γ, a, média móvel à vista, e as
 * demais num «mais» — cada uma com o ⓘ (a fórmula e a referência transcritas do motor, `formulas.ts`). As
 * que o motor 1.0.0 não usa (Ic de corte, Qtn,cs limite, Q, grau de carga do Es, pré-furo e o ΔIc) ficam só
 * para ler, «sem efeito no motor 1.0.0»: deixá-las mudar seria mentir na tela. O u₀ (o modo e os pontos) é da
 * vertical e também fica só para ler aqui, «muda na Edição de cada vertical» (rodada final da 313). A tela
 * recalcula a cada mudança e mostra o antes (o gravado) e o depois; mudar uma premissa de valor exigido diz
 * quantas grandezas saem da homologação (não vão ao PDF nem ao Portal) — a mesma palavra da aba Premissas.
 *
 * Gravar pede o motivo e o destino (R10), e manda SEMPRE as premissas inteiras a partir do gravado:
 *  · «Gravar nesta vertical»: as exceções da vertical mais o que mudou; o NA e a cota pelo cabeçalho; a edição;
 *  · «Gravar na campanha»: só as premissas da campanha — o NA, a cota, o u₀ e a edição são da vertical e ficam
 *    no rascunho, e a janela diz isso (e diz a exceção da vertical que continua valendo nela).
 * Os dois «Gravar» indisponíveis ficam no teclado (`aria-disabled`) com o motivo ao lado (P-UI-25); um campo que
 * não se lê bloqueia a gravação, dizendo qual (como na Edição). Sem `cptu.editar`, ou com a campanha fora de
 * rascunho/devolvida, os dois ficam à vista, bloqueados com o motivo (P-UI-24). «Sair sem gravar» (P-TXT-09:
 * «Descartar» é invalidar com motivo) fecha o rascunho.
 */

/**
 * As premissas que são da vertical por natureza e mudam só na Edição dela: o u₀ não hidrostático (o modo e os
 * pontos). No «Simular» e na aba Premissas ficam para ler, e «Gravar na campanha» nunca as leva (rodada final da 313).
 */
export const PREMISSAS_DA_EDICAO: readonly (keyof Premissas)[] = ['u0Modo', 'u0Pontos'];

/** A frase das que mudam só na Edição de cada vertical. */
export const MUDA_NA_EDICAO = 'muda na Edição de cada vertical';

/** As premissas do rascunho que a campanha pode receber (sem as da Edição de cada vertical). */
export function premissasDaCampanha(p: Partial<Premissas> | undefined): Partial<Premissas> {
  const saida: Partial<Premissas> = { ...(p ?? {}) };
  for (const k of PREMISSAS_DA_EDICAO) delete saida[k];
  return saida;
}

/** As premissas à vista; as outras que mudam vão em «As demais premissas». */
const PRINCIPAIS: readonly (keyof Premissas)[] = ['na', 'nkt', 'kocr', 'phiCv', 'gamaModo', 'gamaFixo', 'a', 'mediaMovel'];

/** Os grupos, na ordem da prancheta. */
const GRUPOS: readonly GrupoDePremissa[] = [
  'Cone e água', 'Peso específico e normalização', 'Argilas', 'Areias', 'Deformabilidade e SPT', 'Dissipação',
];

/** As opções escritas das premissas de escolha. */
const OPCOES: Partial<Record<keyof Premissas, readonly [string, string][]>> = {
  gamaModo: [['robertson2010', 'automático (R&C 2010)'], ['fixo', 'fixo']],
  u0Modo: [['hidrostatico', 'hidrostático'], ['pontos', 'por pontos']],
};

/** O rótulo do campo: «NA (m)», «Nkt», «Peso específico γ», «Raio do cone, r (cm)». */
function rotuloDoCampo(f: FormulaDaPremissa): string {
  if (f.chave === 'na') return 'NA (m)';
  if (f.chave === 'cota') return 'Cota da boca (m)';
  if (f.chave === 'raioCone') return `${f.rotulo} (cm)`;
  if (f.chave === 'a') return 'Razão de área, a';
  return f.unidade && f.unidade !== 'm × kPa' ? `${f.rotulo} (${f.unidade})` : f.rotulo;
}

/** O raio do cone é em metros no motor e em centímetros na tela (1,83 cm, o do .cpt — spec §5.1). */
const ESCALA_NA_TELA: Partial<Record<keyof Premissas, number>> = { raioCone: 100 };

/** Um valor de premissa escrito: o número com vírgula, sim/não, a opção por extenso, a lista contada. */
export function textoDaPremissa(chave: keyof Premissas, v: unknown): string {
  if (v === null || v === undefined) return chave === 'cnCorte' ? 'desligado' : 'não informado';
  if (typeof v === 'boolean') return v ? 'sim' : 'não';
  if (typeof v === 'number') {
    if (chave === 'na' || chave === 'cota') return `${numero(v, 2)} m`;
    if (chave === 'raioCone') return `${numero(v * 100, 2)} cm`;
    return numero(v, 4, true);
  }
  if (Array.isArray(v)) return plural(v.length, 'ponto', 'pontos');
  const opcao = OPCOES[chave]?.find(([valor]) => valor === v);
  return opcao ? opcao[1] : String(v);
}

/**
 * As condições de valor exigido (RN-252) que estas premissas deixam: o rótulo e quantas grandezas homologadas cada uma
 * tira do PDF e do Portal — a conta única do visual (`colunasQueSaemDaHomologacao`, a mesma da aba Premissas; rodada
 * da 313.5), com só a premissa da condição.
 */
function condicoesQueSaem(p: Premissas): { rotulo: string; grandezas: number }[] {
  return CONFORMIDADE.condicoes
    .filter((c) => c.efeito === 'valor_exigido' && p[c.premissa] !== c.valor)
    .map((c) => ({ rotulo: c.rotulo, grandezas: colunasQueSaemDaHomologacao({ [c.premissa]: p[c.premissa] }).length }))
    .filter((c) => c.grandezas > 0);
}

/** O que o campo numérico lê do texto: o número, null (vazio que vale «sem valor») ou undefined (não se lê). */
function lerOCampo(texto: string, opcional: boolean): number | null | undefined {
  if (texto.trim() === '') return opcional ? null : undefined;
  const n = paraNumero(texto);
  return n !== undefined && Number.isFinite(n) ? n : undefined;
}

export function SimularPremissas({ c, vertical, gravadas, rascunho, aoMudar, aoDescartar, aoGravado }: {
  c: CampanhaCompleta;
  vertical: VerticalCompleta;
  /** As premissas efetivas do gravado (o «antes»). */
  gravadas: Premissas;
  rascunho: Rascunho | null;
  aoMudar(r: Rascunho | null): void;
  aoDescartar(): void;
  /** Gravado: `resto` é o que fica no rascunho (o NA, a cota, o u₀ e a edição, quando a gravação foi na campanha). */
  aoGravado(resto: Rascunho | null): Promise<void>;
}) {
  const { pode, eu } = useSessao();
  const [textos, setTextos] = useState<Partial<Record<keyof Premissas, string>>>({});
  const [infoAberta, setInfo] = useState<keyof Premissas | null>(null);
  const [destino, setDestino] = useState<'vertical' | 'campanha' | null>(null);
  const idDaVertical = useId();
  const idDaCampanha = useId();
  const editavel = campanhaEditavel(c.campanha.situacao);
  // A janela de gravar devolve o foco a quem a abriu; gravada (o Simular fecha), ele vai ao «Simular» da barra.
  useFocoDeVolta(destino !== null, '[data-abre="simular"]');

  // O que está valendo no rascunho: o gravado coberto pelo que mudou.
  const mudancas: Partial<Premissas> = { ...(rascunho?.premissas ?? {}) };
  const naGravado = vertical.nivel_agua;
  const valendo: Premissas = {
    ...gravadas, ...mudancas,
    na: rascunho?.nivelAgua !== undefined ? (rascunho.nivelAgua ?? PREMISSAS_PADRAO.na) : gravadas.na,
    cota: rascunho?.cota !== undefined ? rascunho.cota : gravadas.cota,
  };
  const temPremissa = Object.keys(mudancas).length > 0;
  // A campanha recebe só as premissas dela: o u₀ (da Edição), o NA, a cota e a edição são da vertical.
  const temDaCampanha = Object.keys(premissasDaCampanha(mudancas)).length > 0;
  const temDaVertical = rascunho?.nivelAgua !== undefined || rascunho?.cota !== undefined || rascunho?.edicao !== undefined
    || PREMISSAS_DA_EDICAO.some((k) => k in mudancas);
  const saem = condicoesQueSaem(valendo);

  /** Uma premissa mudou: entra no rascunho se difere do gravado; igual a ele, sai. */
  const mudarPremissa = (chave: keyof Premissas, valor: unknown) => {
    const proximo: Rascunho = { ...(rascunho ?? {}), premissas: { ...(rascunho?.premissas ?? {}) } };
    if (chave === 'na') {
      const v = valor as number | null;
      if (v === naGravado) delete proximo.nivelAgua; else proximo.nivelAgua = v;
    } else if (chave === 'cota') {
      const v = valor as number | null;
      if (v === (vertical.cota ?? null)) delete proximo.cota; else proximo.cota = v;
    } else if (JSON.stringify(valor) === JSON.stringify(gravadas[chave])) {
      delete (proximo.premissas as Record<string, unknown>)[chave];
    } else {
      (proximo.premissas as Record<string, unknown>)[chave] = valor;
    }
    if (proximo.premissas && Object.keys(proximo.premissas).length === 0) delete proximo.premissas;
    aoMudar(Object.keys(proximo).length > 0 ? proximo : null);
  };

  /** O campo de uma premissa, pelo tipo dela. */
  const campoDaPremissa = (f: FormulaDaPremissa) => {
    const chave = f.chave;
    const rotulo = rotuloDoCampo(f);
    const gravado = chave === 'na' ? naGravado : chave === 'cota' ? (vertical.cota ?? null) : gravadas[chave];
    const atual = valendo[chave];
    const mudou = JSON.stringify(atual) !== JSON.stringify(chave === 'na' && naGravado === null ? PREMISSAS_PADRAO.na : gravado);
    const info = (
      <button type="button" className="cptu-info" aria-expanded={infoAberta === chave} aria-label={`A fórmula: ${f.rotulo}`}
        title="A fórmula e a referência" onClick={() => setInfo((x) => (x === chave ? null : chave))}>ⓘ</button>
    );
    let entrada;
    if (PREMISSAS_DA_EDICAO.includes(chave)) {
      // O u₀ não hidrostático é da vertical: muda na Edição dela (o que está valendo, com o rascunho de lá).
      entrada = (
        <p className="cptu-premissa-leitura">
          <span className="cptu-premissa-rotulo">{rotulo}</span> {textoDaPremissa(chave, atual)}
          <span className="cptu-nota"> · {MUDA_NA_EDICAO}</span>
        </p>
      );
    } else if (chave === 'gamaFixo' && valendo.gamaModo !== 'fixo') {
      // O γ fixo só conta com o peso específico «fixo»: no automático, para ler, com a nota (rodada do agent-browser).
      entrada = (
        <p className="cptu-premissa-leitura">
          <span className="cptu-premissa-rotulo">{rotulo}</span> {textoDaPremissa(chave, atual)}
          <span className="cptu-nota"> · só vale com o peso específico «fixo»</span>
        </p>
      );
    } else if (f.naTela === 'so_leitura') {
      entrada = (
        <p className="cptu-premissa-leitura">
          <span className="cptu-premissa-rotulo">{rotulo}</span> {textoDaPremissa(chave, gravado)}
          <span className="cptu-nota"> · sem efeito no motor 1.0.0</span>
        </p>
      );
    } else if (OPCOES[chave]) {
      entrada = (
        <label className="campo">
          <span className="rotulo">{rotulo}</span>
          <select value={String(atual)} onChange={(e) => mudarPremissa(chave, e.target.value)}>
            {OPCOES[chave]!.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </label>
      );
    } else if (typeof PREMISSAS_PADRAO[chave] === 'boolean') {
      entrada = (
        <label className="cptu-caixa-premissa">
          <input type="checkbox" checked={atual === true} onChange={(e) => mudarPremissa(chave, e.target.checked)} />
          <span>{rotulo}</span>
        </label>
      );
    } else if (chave === 'cnCorte') {
      const ligado = atual !== null;
      entrada = (
        <div className="cptu-premissa-composta">
          <label className="cptu-caixa-premissa">
            <input type="checkbox" checked={ligado} onChange={(e) => mudarPremissa(chave, e.target.checked ? 1.7 : null)} />
            <span>{rotulo} ligado</span>
          </label>
          {ligado && campoNumerico(f, rotulo)}
        </div>
      );
    } else {
      entrada = campoNumerico(f, rotulo);
    }
    return (
      <div key={chave} className={`cptu-premissa${mudou ? ' mudou' : ''}`} data-premissa={chave}>
        <div className="cptu-premissa-linha">
          {entrada}
          {info}
        </div>
        {mudou && <p className="cptu-premissa-gravado">gravado: {textoDaPremissa(chave, gravado)}</p>}
        {infoAberta === chave && (
          <div className="cptu-formula" role="note">
            <p><b>{f.rotulo}</b>{f.naTela === 'so_leitura' ? ' · sem efeito no motor 1.0.0' : ''}</p>
            <p className="mono">{f.formula}</p>
            <p>Fonte: {f.referencia}</p>
            {f.aplicabilidade && <p>Vale: {f.aplicabilidade}</p>}
          </div>
        )}
      </div>
    );
  };

  /** O campo numérico: o texto como foi digitado; o número que se lê vai ao rascunho. */
  function campoNumerico(f: FormulaDaPremissa, rotulo: string) {
    const chave = f.chave;
    const escala = ESCALA_NA_TELA[chave] ?? 1;
    const opcional = chave === 'na' || chave === 'cota';
    const v = valendo[chave];
    const texto = textos[chave] ?? (chave === 'na' && rascunho?.nivelAgua === undefined && naGravado === null
      ? '' : typeof v === 'number' ? paraCampo(v * escala, 4) : '');
    const erro = lerOCampo(texto, opcional) === undefined ? 'Não se lê: use vírgula para as casas.' : undefined;
    return (
      <CampoNumerico rotulo={rotulo} value={texto} erro={erro} inteiro={chave === 'mediaMovel'}
        onChange={(e) => {
          const t = e.target.value;
          setTextos((x) => ({ ...x, [chave]: t }));
          const n = lerOCampo(t, opcional);
          if (n === null) mudarPremissa(chave, null);
          else if (n !== undefined) mudarPremissa(chave, n / escala);
        }} />
    );
  }

  /*
   * O campo que não se lê fica com o último número que se leu no rascunho: gravar assim gravaria outra coisa que a
   * tela mostra. Ele bloqueia os dois «Gravar», dizendo qual (como a Edição e as Camadas). Só os campos à vista: o
   * corte de Cn desligado não tem campo.
   */
  const naoSeLeem = (Object.keys(textos) as (keyof Premissas)[])
    .filter((k) => !(k === 'cnCorte' && valendo.cnCorte === null) && !(k === 'gamaFixo' && valendo.gamaModo !== 'fixo'))
    .filter((k) => lerOCampo(textos[k] ?? '', k === 'na' || k === 'cota') === undefined)
    .map((k) => `«${FORMULAS_DAS_PREMISSAS[k] ? rotuloDoCampo(FORMULAS_DAS_PREMISSAS[k]) : k}» não se lê: use vírgula para as casas`);

  const porGrupo = (lista: FormulaDaPremissa[]) => GRUPOS
    .map((g) => ({ g, itens: lista.filter((f) => f.grupo === g) }))
    .filter((x) => x.itens.length > 0);
  const todas = Object.values(FORMULAS_DAS_PREMISSAS);
  const principais = PRINCIPAIS.map((k) => FORMULAS_DAS_PREMISSAS[k]);
  const demais = todas.filter((f) => !PRINCIPAIS.includes(f.chave));

  const bloqueio = !pode('cptu.editar')
    ? { tipo: 'permissao' as const }
    : !editavel ? { tipo: 'parada' as const } : null;
  // O que não deixa gravar, dito ao lado de cada «Gravar» (P-UI-25): o campo que não se lê, ou nada a levar.
  const conferir = naoSeLeem.length > 0
    ? `Confira antes de gravar: ${naoSeLeem[0]}${naoSeLeem.length > 1 ? ` (e mais ${naoSeLeem.length - 1})` : ''}.` : null;
  const motivoDaVertical = conferir ?? (!temPremissa && !temDaVertical ? 'Nada mudou ainda.' : null);
  const motivoDaCampanha = conferir ?? (temDaCampanha ? null : temPremissa || temDaVertical
    ? 'O NA, a cota, o u₀ e a edição são da vertical: não vão para a campanha.' : 'Nada mudou ainda.');
  const motivoBloqueado = bloqueio
    ? motivoDoBloqueio(bloqueio.tipo, 'não grava a interpretação do CPTu', c.campanha.situacao, eu?.atuandoPor ? eu.nome : null)
    : '';

  return (
    <section className="cptu-simular" aria-label="Simular premissas">
      <div className="cptu-simular-cabeca">
        <h3>Simular</h3>
        <p className="cptu-nota">
          O gravado aparece em cinza tracejado (o antes); o que você muda aqui recalcula na hora (o depois). Nada
          se grava sem «Gravar».
        </p>
      </div>
      <div className="cptu-simular-campos">
        {principais.map(campoDaPremissa)}
      </div>
      <details className="cptu-simular-mais">
        <summary>As demais premissas</summary>
        {porGrupo(demais).map(({ g, itens }) => (
          <fieldset key={g} className="cptu-simular-grupo">
            <legend>{g}</legend>
            <div className="cptu-simular-campos">{itens.map(campoDaPremissa)}</div>
          </fieldset>
        ))}
      </details>
      {saem.length > 0 && (
        <Aviso tipo="atencao" titulo="▲ Fora das condições homologadas">
          {saem.map((s) => `${s.rotulo} (${plural(s.grandezas, 'grandeza', 'grandezas')})`).join('; ')}. Essas grandezas
          ficam marcadas «▲ não homologada» na tela e não vão ao PDF nem ao Portal.
        </Aviso>
      )}
      <div className="cptu-simular-acoes">
        <button type="button" className="botao secundario" onClick={() => {
          aoDescartar();
          // O Simular fecha: o foco vai ao «Simular» da barra (rodada do agent-browser da 313).
          focarOPrimeiro('[data-abre="simular"]');
        }}>Sair sem gravar</button>
        {/* As duas à vista, bloqueadas, com o motivo uma vez só (P-UI-24; rodada do agent-browser). */}
        {bloqueio ? <AcoesBloqueadas textos={['Gravar nesta vertical', 'Gravar na campanha']} motivo={motivoBloqueado} /> : (
          <>
            <button type="button" className="botao" aria-disabled={motivoDaVertical ? true : undefined}
              aria-describedby={motivoDaVertical ? idDaVertical : undefined}
              onClick={() => { if (!motivoDaVertical) setDestino('vertical'); }}>Gravar nesta vertical</button>
            <button type="button" className="botao secundario" aria-disabled={motivoDaCampanha ? true : undefined}
              aria-describedby={motivoDaCampanha ? (motivoDaCampanha === motivoDaVertical ? idDaVertical : idDaCampanha) : undefined}
              onClick={() => { if (!motivoDaCampanha) setDestino('campanha'); }}>Gravar na campanha</button>
            {(motivoDaVertical || motivoDaCampanha) && (
              <small className="cptu-motivo-bloqueio">
                {motivoDaVertical && <span id={idDaVertical}>{motivoDaVertical} </span>}
                {motivoDaCampanha && motivoDaCampanha !== motivoDaVertical && <span id={idDaCampanha}>{motivoDaCampanha}</span>}
              </small>
            )}
          </>
        )}
      </div>
      {destino && rascunho && (
        <JanelaDeGravar destino={destino} c={c} vertical={vertical} gravadas={gravadas} rascunho={rascunho}
          aoFechar={() => setDestino(null)}
          aoPronto={async (resto) => { setDestino(null); setTextos({}); await aoGravado(resto); }} />
      )}
    </section>
  );
}

/**
 * O motivo e o destino da gravação (R10). Na vertical: as exceções inteiras (com o u₀ da Edição, se houver), o NA e a
 * cota pelo cabeçalho, a edição. Na campanha: as premissas da campanha inteiras, sem o u₀ — e a janela lista o que
 * fica no rascunho (o NA, a cota, o u₀, a edição) e a exceção da vertical que continua valendo nela.
 */
function JanelaDeGravar({ destino, c, vertical, gravadas, rascunho, aoFechar, aoPronto }: {
  destino: 'vertical' | 'campanha';
  c: CampanhaCompleta;
  vertical: VerticalCompleta;
  gravadas: Premissas;
  rascunho: Rascunho;
  aoFechar(): void;
  aoPronto(resto: Rascunho | null): Promise<void>;
}) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const todas = rascunho.premissas ?? {};
  // Na campanha vão só as premissas dela: o u₀ da Edição fica no rascunho, com o NA, a cota e a edição.
  const mudancas = destino === 'campanha' ? premissasDaCampanha(todas) : todas;
  const chaves = Object.keys(mudancas) as (keyof Premissas)[];
  const rotuloDe = (k: keyof Premissas) => FORMULAS_DAS_PREMISSAS[k]?.rotulo ?? k;
  const linhas = chaves.map((k) => `${rotuloDe(k)}: ${textoDaPremissa(k, gravadas[k])} → ${textoDaPremissa(k, mudancas[k])}`);
  const daVertical: string[] = [];
  if (rascunho.nivelAgua !== undefined) daVertical.push(`NA ${textoDaPremissa('na', rascunho.nivelAgua)}`);
  if (rascunho.cota !== undefined) daVertical.push(`cota ${textoDaPremissa('cota', rascunho.cota)}`);
  if (PREMISSAS_DA_EDICAO.some((k) => k in todas)) {
    const modo = todas.u0Modo ?? gravadas.u0Modo;
    const pontos = todas.u0Pontos ?? gravadas.u0Pontos;
    daVertical.push(`o u₀ (${textoDaPremissa('u0Modo', modo)}${modo === 'pontos' ? `: ${textoDaPremissa('u0Pontos', pontos)}` : ''})`);
  }
  if (rascunho.edicao !== undefined) daVertical.push('a edição');
  const excecoes = excecoesDaVertical(vertical) as Record<string, unknown>;
  const continuam = destino === 'campanha' ? chaves.filter((k) => k in excecoes) : [];
  const falta = motivo.trim().length < 10 ? 'Diga o motivo (pelo menos 10 caracteres): ele vai para a trilha.' : null;

  const gravar = async () => {
    if (falta || ocupado) return;
    setOcupado(true);
    setErro(null);
    try {
      if (destino === 'vertical') {
        const corpo: AlteracaoDaVertical = { motivo: motivo.trim() };
        if (chaves.length > 0) corpo.premissas = premissasParaAVertical(vertical, mudancas);
        if (rascunho.nivelAgua !== undefined) corpo.nivelAgua = rascunho.nivelAgua;
        if (rascunho.cota !== undefined) corpo.cota = rascunho.cota;
        if (rascunho.edicao !== undefined) corpo.edicao = rascunho.edicao;
        await alterarVertical(vertical.id, corpo);
        await aoPronto(null);
      } else {
        await alterarCampanha(c.campanha.id, { premissas: premissasParaACampanha(c, mudancas), motivo: motivo.trim() });
        const resto: Rascunho = {};
        const doU0 = Object.fromEntries(PREMISSAS_DA_EDICAO.filter((k) => k in todas).map((k) => [k, todas[k]]));
        if (Object.keys(doU0).length > 0) resto.premissas = doU0 as Partial<Premissas>;
        if (rascunho.nivelAgua !== undefined) resto.nivelAgua = rascunho.nivelAgua;
        if (rascunho.cota !== undefined) resto.cota = rascunho.cota;
        if (rascunho.edicao !== undefined) resto.edicao = rascunho.edicao;
        await aoPronto(Object.keys(resto).length > 0 ? resto : null);
      }
    } catch (e) {
      setErro(e);
      setOcupado(false);
    }
  };

  const titulo = destino === 'vertical' ? 'Gravar nesta vertical' : 'Gravar na campanha';
  return (
    <Dialogo aberto titulo={titulo} aoFechar={aoFechar}
      rotulo={destino === 'vertical' ? `Vertical ${vertical.codigo}` : `Campanha ${codigoDaCampanha(c.campanha.sequencia)}`}>
      <FormularioDaJanela aoEnviar={() => { void gravar(); }}>
        <ErroDaApi erro={erro} />
        <p className="sub">
          {destino === 'vertical'
            ? `Vai para a vertical ${vertical.codigo}: as exceções dela à campanha, inteiras. As leituras não mudam.`
            : `Vai para a campanha ${codigoDaCampanha(c.campanha.sequencia)}: vale em toda vertical que não tem exceção. `
              + 'As leituras não mudam.'}
        </p>
        <ul className="cptu-lista-da-janela">
          {linhas.map((l) => <li key={l}>{l}</li>)}
          {destino === 'vertical' && daVertical.map((l) => <li key={l}>{l}</li>)}
        </ul>
        {destino === 'campanha' && daVertical.length > 0 && (
          <Aviso tipo="info" titulo="Ficam no rascunho, para «Gravar nesta vertical»">
            {daVertical.join(' · ')}. O NA, a cota, o u₀ e a edição são da vertical: a campanha não os leva.
          </Aviso>
        )}
        {continuam.map((k) => (
          <Aviso key={k} tipo="info">
            A vertical {vertical.codigo} tem exceção em {rotuloDe(k)} ({textoDaPremissa(k, excecoes[k])}): nela continua
            valendo a exceção.
          </Aviso>
        ))}
        <Area rotulo="Motivo" rows={2} value={motivo} maxLength={2000}
          dica="Vai para a trilha, com quem gravou e quando." onChange={(e) => setMotivo(e.target.value)} />
        {falta && <p className="cptu-falta" role="status">{falta}</p>}
        <div className="rodape-dialogo">
          <button type="submit" className="botao" aria-disabled={!!falta || ocupado}>
            {ocupado ? 'Gravando…' : 'Gravar'}
          </button>
          <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
        </div>
      </FormularioDaJanela>
    </Dialogo>
  );
}
